import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {TravelProfile} from "../../models/flightSearchData";
import {useTranslation} from "../../i18n/LanguageProvider";
import {matchesCountry} from "../../utils/countrySearch";
import passportCountries from "./passportCountries.json";

interface Props {
  value?: TravelProfile | null;
  onChange: (profile: TravelProfile | null) => void;
}

export default function PassportSelector({value, onChange}: Props) {
  const {t, locale} = useTranslation();
  const [query, setQuery] = useState("");
  const id = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([locale], {type: "region"});
    return passportCountries.map(code => ({code, name: names.of(code) || code}))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);
  const matching = countries.filter(country => matchesCountry(country.code, query));
  const selectedCountry = countries.find(country => country.code === value?.passportCountry);
  useEffect(() => {
    if (open) listRef.current?.children[activeIndex]?.scrollIntoView?.({block: "nearest"});
  }, [activeIndex, open]);
  const closeList = () => {setOpen(false); setQuery(""); setActiveIndex(0);};
  const selectCountry = (code: string) => {
    onChange({passportCountry: code, visaFreeOnly: value?.visaFreeOnly || false, easyVisaOnly: value?.easyVisaOnly || false});
    closeList();
  };

  return (
    <details className="mt-5 border-t border-purple-100 pt-4 text-left">
      <summary className="cursor-pointer rounded-lg py-2 text-sm font-semibold text-accent focus-visible:outline focus-visible:outline-2">
        {t("visa.title")}{value ? ` · ${countries.find(c => c.code === value.passportCountry)?.name || value.passportCountry}` : ""}
      </summary>
      <div className="mt-3 max-w-2xl space-y-3 rounded-xl bg-white p-4">
        <label htmlFor={`${id}-search`} className="block text-sm font-bold text-primary">{t("visa.passport")}</label>
        <div className="relative" onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closeList();
        }}>
          <input id={`${id}-search`} role="combobox" aria-autocomplete="list"
            aria-expanded={open} aria-controls={`${id}-options`}
            aria-activedescendant={open && matching[activeIndex] ? `${id}-${matching[activeIndex].code}` : undefined}
            autoComplete="off"
            value={open ? query : selectedCountry ? `${selectedCountry.name} (${selectedCountry.code})` : ""}
            onFocus={() => {setOpen(true); setActiveIndex(0);}}
            onClick={() => setOpen(true)}
            onChange={event => {setQuery(event.target.value); setOpen(true); setActiveIndex(0);}}
            onKeyDown={event => {
              if (event.key === "Escape") {event.preventDefault(); closeList();}
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                setOpen(true);
                setActiveIndex(index => !open ? 0 : Math.max(0, Math.min(matching.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
              }
              if (event.key === "Enter" && open) {
                event.preventDefault();
                if (matching[activeIndex]) selectCountry(matching[activeIndex].code);
              }
            }}
            placeholder={t("common.searchCountry")} className="min-h-[44px] w-full rounded-xl border border-gray-300 px-3 py-2 pr-9 text-sm text-primary focus:border-purple-500 focus:outline-none" />
          <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 text-gray-500">▾</span>
          {open && <div ref={listRef} id={`${id}-options`} role="listbox" aria-label={t("visa.searchResults")}
            className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
            {matching.map((country, index) => <button key={country.code} id={`${id}-${country.code}`} type="button"
              role="option" aria-selected={country.code === value?.passportCountry} tabIndex={-1}
              onMouseDown={event => event.preventDefault()} onClick={() => selectCountry(country.code)}
              className={`block min-h-[44px] w-full px-3 py-2 text-left text-sm text-primary hover:bg-purple-50 ${index === activeIndex ? "bg-purple-50" : ""}`}>
              {country.name} ({country.code})
            </button>)}
            {matching.length === 0 && <p role="status" className="p-3 text-sm text-gray-600">{t("destination.emptyCountries")}</p>}
          </div>}
        </div>
        {value && <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm font-semibold text-primary">
          <input type="checkbox" checked={value.visaFreeOnly} onChange={event => onChange({...value, visaFreeOnly: event.target.checked, easyVisaOnly: false})}
            className="h-4 w-4 accent-purple-700" />
          {t("visa.onlyFree")}
        </label>}
        {value && <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm font-semibold text-primary">
          <input type="checkbox" checked={!!value.easyVisaOnly && !value.visaFreeOnly}
            onChange={event => onChange({...value, easyVisaOnly: event.target.checked, visaFreeOnly: false})}
            className="h-4 w-4 accent-orange-600" />
          {t("visa.onlyEasy")}
        </label>}
        {value && <button type="button" onClick={() => {onChange(null); closeList();}} className="min-h-[44px] text-sm font-semibold text-accent hover:underline">{t("visa.remove")}</button>}
      </div>
    </details>
  );
}
